import { ClientOnly } from "@tanstack/react-router";
import { Suspense, lazy } from "react";

import { AffiliatesFAQ } from "../components/AffiliatesFAQ";
import FallbackBackground from "../components/FallbackBackground";
import { FinalCTASection } from "../components/FinalCTA";
import FinalCTAMobile from "../components/FinalCTAMobile";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Testimonials from "../components/Testimonials";
import { ROUTES } from "../routes";

// Same degradation strategy as Pricing: the static gradient below always
// paints, so a purged chunk degrades to nothing instead of crashing the route.
const ScrollReactiveBackground = lazy(() =>
  import("../components/ScrollReactiveBackground").catch(() => ({
    default: () => <></>,
  })),
);

const STEPS = [
  {
    title: "Sign up",
    body: "Create an account on Flowstep, then navigate to the referral dashboard in the settings page.",
  },
  {
    title: "Share",
    body: "Spread the word about Flowstep using your unique link. Monitor the performance in real-time.",
  },
  {
    title: "Earn",
    body: "Get paid monthly for every successful referral you bring. Every referral gets $5 off their first purchase too.",
  },
];

export default function Affiliates() {
  return (
    <div className="relative min-h-screen">
      {/* Static gradient — paints immediately on SSR, then the WebGL canvas
          layers on top once its chunk has loaded. */}
      <FallbackBackground variant="dark" />

      <ClientOnly>
        <Suspense fallback={null}>
          <ScrollReactiveBackground variant="dark" />
        </Suspense>
      </ClientOnly>

      {/* Content wrapper with relative positioning */}
      <div className="dark text-foreground relative z-10 pt-20 pb-16 lg:pb-0">
        {/* Navigation Header */}
        <Header />

        <FinalCTASection
          text="Affiliate program"
          subtext="Get rewarded for spreading the word about Flowstep"
          primaryButton={{
            text: "Join the Program",
            route: ROUTES.registerAsAffiliate,
          }}
          headingLevel="h1"
        />

        {/* How it works */}
        <section className="container mx-auto max-w-7xl px-4 pb-24 md:pb-32">
          {/* How it works heading */}
          <div className="mb-16 text-center md:mb-20">
            <h2
              className="mb-4 font-semibold text-white"
              style={{ fontSize: "clamp(2rem, 5vw, 3rem)" }}
            >
              Get 20% commission for 3 months
            </h2>
            <p
              className="mx-auto max-w-2xl text-gray-300"
              style={{ fontSize: "clamp(1rem, 2vw, 1.25rem)" }}
            >
              Start earning commissions in three simple steps
            </p>
          </div>

          <div className="mx-auto grid max-w-6xl divide-y divide-white/10 md:grid-cols-3 md:divide-x md:divide-y-0">
            {STEPS.map((step, index) => (
              <div
                key={step.title}
                className="flex flex-col gap-3 py-8 md:px-8 md:py-2"
              >
                <span
                  aria-hidden
                  className="font-mono text-sm font-semibold text-[#6b96ff]"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="text-xl font-semibold text-white">
                  {step.title}
                </h3>
                <p className="leading-relaxed text-gray-400">{step.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <Testimonials />

        {/* FAQ Section */}
        <AffiliatesFAQ />

        {/* Final CTA */}
        <FinalCTASection
          text="Ready to start earning?"
          subtext="Join our affiliate program and get 20% commission for 3 months"
          primaryButton={{
            text: "Join the Program",
            route: ROUTES.registerAsAffiliate,
          }}
        />

        {/* Footer */}
        <Footer />
      </div>

      <FinalCTAMobile />
    </div>
  );
}
